import { useState, useEffect, useRef } from "react";
import { Link } from "react-router";
import { useI18n } from "~/i18n";
import { useLenis } from "~/hooks/use-lenis";
import { useRevealOnScroll } from "~/hooks/use-reveal-on-scroll";
import { LanguageSwitcher } from "~/components/language-switcher";
import { ORIGINALS_LIST } from "~/data/ip";

export function Home() {
  const { locale, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bizInfoOpen, setBizInfoOpen] = useState(false);
  const [dimOpacity, setDimOpacity] = useState(0.2);
  const [currentVideoIdx, setCurrentVideoIdx] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursorRingRef = useRef<HTMLDivElement>(null);

  const lenisRef = useLenis(true);

  // 커스텀 커서
  useEffect(() => {
    const cursor = cursorRef.current;
    const ring = cursorRingRef.current;
    if (!cursor || !ring) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    document.documentElement.classList.add("has-custom-cursor");
    cursor.style.display = "block";
    ring.style.display = "block";

    let mx = 0,
      my = 0,
      rx = 0,
      ry = 0;
    let raf: number;

    const onMove = (e: MouseEvent) => {
      mx = e.clientX;
      my = e.clientY;
      cursor.style.transform = `translate(${mx}px, ${my}px)`;
    };

    const animate = () => {
      rx += (mx - rx) * 0.1;
      ry += (my - ry) * 0.1;
      ring.style.transform = `translate(${rx}px, ${ry}px)`;
      raf = requestAnimationFrame(animate);
    };

    const onOver = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("a, button, [role='button']")) {
        cursor.classList.add("home-cursor--hover");
        ring.classList.add("home-cursor-ring--hover");
      }
    };
    const onOut = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("a, button, [role='button']")) {
        cursor.classList.remove("home-cursor--hover");
        ring.classList.remove("home-cursor-ring--hover");
      }
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    raf = requestAnimationFrame(animate);

    return () => {
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Hero PV 영상: 랜덤 시작 + 연속 재생
  const HERO_VIDEOS: Array<{
    src: string;
    title: { ko: string; en: string };
    thumbnail: string;
    youtubeUrl: string;
  }> = [
    {
      src: "/videos/용한소녀.mp4",
      title: { ko: "용한소녀 PV", en: "Brave Girl PV" },
      thumbnail: "/images/ip/posters/용한소녀.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=Iq_zj-KiW2M",
    },
    {
      src: "/videos/위험한 편의점.mp4",
      title: { ko: "위험한 편의점 PV", en: "Dangerous Store PV" },
      thumbnail: "/images/ip/posters/위험한 편의점.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=qoASSVsrFho",
    },
    {
      src: "/videos/호랑이 들어와요.mp4",
      title: { ko: "호랑이 들어와요 PV", en: "Tiger Coming In PV" },
      thumbnail: "/images/ip/posters/호랑이 들어와요.jpg",
      youtubeUrl: "https://youtu.be/fQqRQpGCR1M",
    },
    {
      src: "/videos/마루는 강쥐.mp4",
      title: { ko: "마루는 강쥐 PV", en: "Maru is a Puppy PV" },
      thumbnail: "/images/ip/posters/마루는 강쥐.jpg",
      youtubeUrl: "https://youtu.be/2He3scdIk44",
    },
  ];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let idx = Math.floor(Math.random() * HERO_VIDEOS.length);
    setCurrentVideoIdx(idx);

    const playNext = () => {
      const item = HERO_VIDEOS[idx];
      if (!item) return;
      setCurrentVideoIdx(idx);
      video.src = item.src;
      video.load();
      video.play().catch(() => {});
      idx = (idx + 1) % HERO_VIDEOS.length;
    };

    video.addEventListener("ended", playNext);
    playNext();

    return () => video.removeEventListener("ended", playNext);
  }, []);

  // Hero ↔ About 풀페이지 스냅 + dim 제어
  useEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;

    let locked = false;
    const EASING = (t: number) => 1 - Math.pow(1 - t, 4);

    const onWheel = (e: WheelEvent) => {
      if (locked) return;
      const about = document.getElementById("about");
      if (!about) return;

      const aboutTop = Math.max(0, about.getBoundingClientRect().top + window.scrollY - 80);
      const current = lenis.scroll;
      const heroZone = aboutTop * 0.8;

      if (e.deltaY > 0 && current < heroZone) {
        e.preventDefault();
        locked = true;
        lenis.scrollTo(aboutTop, {
          duration: 0.8,
          easing: EASING,
          force: true,
          lock: true,
          onComplete: () => {
            locked = false;
          },
        });
      }
      if (e.deltaY < 0 && current > 0 && current < aboutTop + 100) {
        const distFromAbout = Math.abs(current - aboutTop);
        if (distFromAbout < 100) {
          e.preventDefault();
          locked = true;
          lenis.scrollTo(0, {
            duration: 0.8,
            easing: EASING,
            force: true,
            lock: true,
            onComplete: () => {
              locked = false;
            },
          });
        }
      }
    };

    // Lenis scroll로 dim 제어
    const unsubscribe = lenis.on("scroll", () => {
      const heroH = window.innerHeight;
      const progress = Math.min(1, Math.max(0, lenis.scroll / (heroH * 0.5)));
      const newOpacity = 0.2 + progress * 0.5;
      setDimOpacity(newOpacity);
    });

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", onWheel);
      unsubscribe();
    };
  }, [lenisRef]);

  useRevealOnScroll(null, { threshold: 0.1 });

  // Header scroll detection
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 80);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { key: "about", href: "#about", label: t.home.navAbout },
    { key: "works", href: "#works", label: t.home.navWorks },
  ];

  return (
    <div className="min-h-dvh bg-[#f0f0f0]">
      {/* ── Mobile fullscreen nav ── */}
      <nav
        className={`hidden max-md:fixed max-md:inset-0 max-md:z-[999] max-md:flex max-md:flex-col max-md:items-center max-md:justify-center max-md:gap-10 max-md:bg-white max-md:transition-transform max-md:duration-[400ms] ${
          menuOpen ? "max-md:translate-x-0" : "max-md:translate-x-full"
        }`}
      >
        <button
          className="absolute top-5 right-6 w-6 h-6 flex items-center justify-center"
          onClick={() => setMenuOpen(false)}
          aria-label="close menu"
        >
          <span className="block w-full h-px bg-[#111] rotate-45 absolute" />
          <span className="block w-full h-px bg-[#111] -rotate-45 absolute" />
        </button>
        {navItems.map((item) => (
          <a
            key={item.key}
            href={item.href}
            className="text-[14px] font-medium tracking-[0.2em] text-[#666] transition-colors hover:text-[#111]"
            onClick={() => setMenuOpen(false)}
          >
            {item.label}
          </a>
        ))}
      </nav>

      {/* ── Header ── */}
      <header
        className="sticky top-0 z-[100] px-10 max-md:px-6 transition-all duration-300 py-6"
        style={{
          background: scrolled
            ? "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%)"
            : "transparent",
        }}
      >
        <div className="mx-auto grid grid-cols-[1fr_auto_1fr] items-center max-md:flex max-md:justify-between">
          <a
            href="#"
            className="flex items-center"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img
              src="/images/laftel_only.webp"
              alt="Laftel Only"
              className="h-[22px] w-auto brightness-0 invert"
            />
          </a>

          {/* Desktop nav */}
          <nav className="flex items-center gap-9 max-md:hidden">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className="relative text-[11px] font-medium tracking-[0.2em] after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-px after:scale-x-0 after:transition-transform after:content-[''] hover:after:scale-x-100 after:bg-white"
                style={{ color: "#fff" }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-5">
            <LanguageSwitcher variant="hero" forceColor="#fff" />
            <button
              className="hidden max-md:flex w-6 flex-col gap-[5px]"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="menu"
            >
              <span className="block w-full h-px" style={{ backgroundColor: "#fff" }} />
              <span className="block w-full h-px" style={{ backgroundColor: "#fff" }} />
              <span className="block w-full h-px" style={{ backgroundColor: "#fff" }} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="sticky top-0 z-0 flex items-center justify-center h-[100dvh] bg-[#111] overflow-hidden -mt-[74px] pt-[74px]">
        {/* PV 배경 영상 */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover transition-[filter] duration-700 ease-out"
          style={{ filter: dimOpacity > 0.3 ? "blur(12px)" : "blur(0px)" }}
          muted
          playsInline
          autoPlay
        />
        {/* dim + 하프톤 패턴 오버레이 */}
        <div
          className="absolute inset-0 bg-black transition-opacity duration-700 ease-out"
          style={{ opacity: dimOpacity }}
        />
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #000 0.8px, transparent 0.8px), radial-gradient(circle, #000 0.8px, transparent 0.8px)",
            backgroundSize: "4px 4px",
            backgroundPosition: "0 0, 2px 2px",
          }}
        />

        {/* 스크롤 인디케이터 */}
        <div
          className="absolute bottom-10 max-md:bottom-[112px] left-1/2 -translate-x-1/2 z-[2] flex flex-col items-center gap-2 transition-opacity duration-500"
          style={{ opacity: dimOpacity > 0.3 ? 0 : 1, color: "rgba(255,255,255,0.4)" }}
        >
          <span
            className="text-[10px] tracking-[0.3em] font-medium"
            style={{ color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            SCROLL
          </span>
          <div
            className="w-px h-8 relative overflow-hidden"
            style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
          >
            <div
              className="absolute inset-x-0 h-full animate-[homeScrollLine_2s_ease_infinite]"
              style={{ backgroundColor: "rgba(255,255,255,0.5)" }}
            />
          </div>
        </div>

        {/* 영상 정보 카드 */}
        {HERO_VIDEOS[currentVideoIdx] && (
          <a
            href={HERO_VIDEOS[currentVideoIdx].youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-10 left-10 max-md:bottom-6 max-md:left-6 max-md:right-6 z-[1] flex items-center gap-4 rounded-[16px] bg-black/40 backdrop-blur-[8px] p-2.5 pr-8 max-md:pr-6 transition-all duration-300 hover:bg-black/60 group"
            style={{
              transform: dimOpacity > 0.3 ? "translateY(30px)" : "translateY(0)",
              opacity: dimOpacity > 0.3 ? 0 : 1,
              pointerEvents: dimOpacity > 0.3 ? "none" : "auto",
              transition: "transform 0.3s ease, opacity 0.3s ease, background-color 0.3s ease",
            }}
          >
            <div className="w-[56px] h-[56px] max-md:w-[44px] max-md:h-[44px] rounded-[8px] overflow-hidden shrink-0">
              <img
                src={HERO_VIDEOS[currentVideoIdx].thumbnail}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              <span
                className="text-[13px] max-md:text-[11px] font-bold text-white leading-tight truncate"
                style={{ color: "#fff" }}
              >
                {locale === "ko"
                  ? HERO_VIDEOS[currentVideoIdx].title.ko
                  : HERO_VIDEOS[currentVideoIdx].title.en}
              </span>
              <span
                className="flex items-center gap-1.5 text-[11px] max-md:text-[10px] text-white/50 font-medium"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 shrink-0">
                  <path d="M21.582 6.186a2.506 2.506 0 00-1.768-1.768C18.254 4 12 4 12 4s-6.254 0-7.814.418A2.506 2.506 0 002.418 6.186C2 7.746 2 12 2 12s0 4.254.418 5.814a2.506 2.506 0 001.768 1.768C5.746 20 12 20 12 20s6.254 0 7.814-.418a2.506 2.506 0 001.768-1.768C22 16.254 22 12 22 12s0-4.254-.418-5.814zM10 15.464V8.536L16 12l-6 3.464z" />
                </svg>
                {t.home.watchOnYoutube}
              </span>
            </div>
          </a>
        )}
      </section>

      {/* ── About ── */}
      <section
        id="about"
        className="relative z-[2] pt-[320px] pb-[160px] max-md:pt-[160px] max-md:pb-[80px] px-6"
      >
        <div className="max-w-[680px] mx-auto text-center">
          <h2 className="reveal-fade reveal-slow text-[28px] max-md:text-[22px] font-bold tracking-[0.1em] mb-6 max-md:mb-4 text-white">
            ABOUT
          </h2>
          <p
            className="reveal-fade reveal-slow text-[20px] max-md:text-[17px] leading-[1.6] text-white font-medium mb-8 max-md:mb-6"
            style={{ transitionDelay: "0.1s" }}
          >
            “{t.home.aboutTitle}”
          </p>
          <p
            className="reveal-fade reveal-slow text-[15px] max-md:text-[14px] leading-[2] text-white/70 font-light whitespace-pre-line"
            style={{ transitionDelay: "0.2s" }}
          >
            {t.home.aboutDescription}
          </p>
        </div>
      </section>

      {/* ── Works ── */}
      <section id="works" className="relative z-[2] py-[160px] max-md:py-[80px] px-6">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14 max-md:mb-8">
            <h2 className="reveal-fade reveal-slow text-[28px] max-md:text-[22px] font-bold tracking-[0.1em] text-white">
              WORKS
            </h2>
          </div>

          <div className="grid grid-cols-4 gap-2 max-md:grid-cols-3 max-md:gap-0.5">
            {[...ORIGINALS_LIST].reverse().map((original) => {
              const isComingSoon = original.comingSoon;
              const href = original.externalUrl ?? `/${original.slug}`;
              const isExternal = !!original.externalUrl;

              const card = (
                <div className="relative aspect-[2/3] overflow-hidden rounded-[4px] bg-[#e8e8e8] shadow-[0_4px_16px_rgba(0,0,0,0.3)]">
                  {original.thumbnail ? (
                    <img
                      src={original.thumbnail}
                      alt={original.title[locale]}
                      className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.05]"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center px-3">
                      <span className="text-[12px] max-md:text-[10px] font-medium text-[#bbb] text-center leading-snug">
                        {original.title[locale]}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 transition-opacity duration-300 ease-out will-change-[opacity] group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 transition-opacity duration-300 ease-out will-change-[opacity] group-hover:opacity-100" />
                  <div className="absolute bottom-4 left-4 right-4 max-md:bottom-2.5 max-md:left-2.5 max-md:right-2.5">
                    <span className="block text-[14px] max-md:text-[11px] font-bold tracking-[0.02em] text-white opacity-0 translate-y-1.5 transition-[opacity,transform] duration-300 ease-out will-change-[opacity,transform] group-hover:opacity-100 group-hover:translate-y-0">
                      {original.title[locale]}
                    </span>
                  </div>
                  {isComingSoon && (
                    <span
                      className="absolute top-0 left-0 inline-flex items-center h-[20px] rounded-br-[4px] bg-black/70 px-2 text-[9px] font-bold tracking-[0.1em] leading-none"
                      style={{ color: "#fff" }}
                    >
                      {t.home.comingSoon}
                    </span>
                  )}
                </div>
              );

              if (isExternal) {
                return (
                  <a
                    key={original.slug}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    {card}
                  </a>
                );
              }

              return (
                <Link key={original.slug} to={href} className="group">
                  {card}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="relative z-[2] bg-black/30">
        <div className="py-10 max-md:py-8 px-6">
          <div className="mx-auto max-w-[860px] flex flex-col items-start gap-4">
            <img
              src="/images/ic_logo_typo.svg"
              alt="Laftel"
              className="h-8 w-auto"
              style={{ filter: "brightness(0) invert(1)", opacity: 0.4 }}
            />
            <div style={{ color: "rgba(255,255,255,0.4)" }}>
              <button
                className="flex items-center gap-1 text-[11px] font-normal tracking-[0.05em] transition-colors hover:text-white/60"
                onClick={() => setBizInfoOpen((v) => !v)}
              >
                (주)라프텔 사업자 정보
                <svg
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className={`w-3 h-3 transition-transform duration-200 ${bizInfoOpen ? "rotate-180" : ""}`}
                >
                  <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-out"
                style={{
                  maxHeight: bizInfoOpen ? "200px" : "0px",
                  opacity: bizInfoOpen ? 1 : 0,
                }}
              >
                <div
                  className="pt-2 text-[11px] font-light leading-[1.8]"
                  style={{ color: "rgba(255,255,255,0.35)" }}
                >
                  <p>상호 : 주식회사 라프텔 / 대표 : 박종원</p>
                  <p>주소 : 서울특별시 영등포구 국제금융로 10, 28층(여의도동, 쓰리아이에프씨)</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <a
                  href="https://laftel.net"
                  className="text-[11px] font-normal tracking-[0.05em] transition-colors"
                  style={{ color: "rgba(255,255,255,0.4)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "rgba(255,255,255,0.4)";
                  }}
                >
                  {locale === "ko" ? "라프텔 바로가기" : "Laftel"}
                </a>
                <span className="text-[12px]" style={{ color: "rgba(255,255,255,0.15)" }}>
                  |
                </span>
                <a
                  href="mailto:contact@laftel.net"
                  className="text-[11px] font-normal tracking-[0.05em] transition-colors"
                  style={{ color: "rgba(255,255,255,0.4)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "rgba(255,255,255,0.4)";
                  }}
                >
                  {t.footer.contact}
                </a>
              </div>
              <p
                className="text-[11px] tracking-[0.05em] font-light"
                style={{ color: "rgba(255,255,255,0.4)" }}
              >
                © {new Date().getFullYear()} Laftel Inc. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Top Button ── */}
      <button
        className="fixed bottom-10 right-10 max-md:bottom-5 max-md:right-4 z-[99] w-[92px] h-[92px] max-md:w-[72px] max-md:h-[72px] rounded-full"
        style={{
          opacity: scrolled ? 1 : 0,
          transform: scrolled ? "translateY(0)" : "translateY(32px)",
          pointerEvents: scrolled ? "auto" : "none",
          transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
        }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
      >
        <div className="relative w-full h-full">
          {/* 회전 텍스트 */}
          <svg
            className="absolute inset-0 w-full h-full animate-[spin_8s_linear_infinite]"
            viewBox="0 0 100 100"
          >
            <defs>
              <path id="topBtnCircle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
            </defs>
            <text fill="white" fontSize="8" fontWeight="600">
              <textPath href="#topBtnCircle" textLength="238" lengthAdjust="spacingAndGlyphs">
                BACK TO TOP · BACK TO TOP ·{" "}
              </textPath>
            </text>
          </svg>
          {/* 중앙 아이콘 */}
          <img
            src="/images/hi_puming.png"
            alt=""
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[52px] h-[52px] max-md:w-[40px] max-md:h-[40px]"
          />
        </div>
      </button>

      {/* ── Custom Cursor ── */}
      <div ref={cursorRef} className="home-cursor" aria-hidden="true" />
      <div ref={cursorRingRef} className="home-cursor-ring" aria-hidden="true" />
    </div>
  );
}
